<template>
  <div class="health-page content-page">
    <section class="health-hero">
      <div class="label">
        <div>
          <span class="eyebrow">AI editorial content</span>
          <h4>Content Management</h4>
        </div>
        <button class="btn-refresh" :disabled="loading" @click="refreshContentDashboard">
          {{ loading ? 'Refreshing...' : 'Refresh' }}
        </button>
      </div>
      <p class="intro">
        Review generated catalog content coverage, find missing editorial fields,
        and enqueue selected parts for AI research without leaving the admin workflow.
      </p>
    </section>

    <div v-if="error" class="alert">
      {{ error }}
      <button class="alert-retry" type="button" @click="fetchContent()">Retry</button>
    </div>
    <div v-if="notice" class="notice">{{ notice }}</div>

    <div class="kpi-row">
      <div class="kpi-card">
        <span class="kpi-label">Total parts</span>
        <span class="kpi-value">{{ kpis.total }}</span>
      </div>
      <div class="kpi-card tone-muted">
        <span class="kpi-label">Missing content</span>
        <span class="kpi-value">{{ kpis.missing }}</span>
      </div>
      <div class="kpi-card tone-warn">
        <span class="kpi-label">Needs review</span>
        <span class="kpi-value">{{ kpis.needsReview }}</span>
      </div>
      <div class="kpi-card tone-bad">
        <span class="kpi-label">Failed</span>
        <span class="kpi-value">{{ kpis.failed }}</span>
      </div>
      <div class="kpi-card tone-good">
        <span class="kpi-label">Completed</span>
        <span class="kpi-value">{{ kpis.completed }}</span>
      </div>
    </div>

    <section class="coverage-progress" aria-labelledby="coverage-progress-title">
      <div class="coverage-progress-head">
        <div>
          <span id="coverage-progress-title" class="coverage-progress-title">Editorial completion</span>
          <span class="coverage-progress-detail">
            {{ coverageProgress.completed }} of {{ coverageProgress.total }} parts completed
          </span>
        </div>
        <strong>{{ coverageProgress.percent }}%</strong>
      </div>
      <div
        class="coverage-progress-track"
        role="progressbar"
        aria-label="Completed editorial content"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuenow="coverageProgress.percent"
        :aria-valuetext="coverageProgress.completed + ' of ' + coverageProgress.total + ' parts completed'"
      >
        <span
          v-if="coverageProgress.completed"
          class="coverage-progress-segment segment-approved"
          :style="{ width: coverageProgress.completedWidth + '%' }"
          :title="coverageProgress.completed + ' completed'"
        ></span>
        <span
          v-if="coverageProgress.inProgress"
          class="coverage-progress-segment segment-progress"
          :style="{ width: coverageProgress.inProgressWidth + '%' }"
          :title="coverageProgress.inProgress + ' in progress'"
        ></span>
        <span
          v-if="coverageProgress.failed"
          class="coverage-progress-segment segment-failed"
          :style="{ width: coverageProgress.failedWidth + '%' }"
          :title="coverageProgress.failed + ' failed'"
        ></span>
      </div>
      <div class="coverage-progress-legend" aria-label="Editorial coverage breakdown">
        <span class="legend-approved"><i></i>Completed {{ coverageProgress.completed }}</span>
        <span class="legend-progress"><i></i>In progress {{ coverageProgress.inProgress }}</span>
        <span class="legend-failed"><i></i>Failed {{ coverageProgress.failed }}</span>
        <span class="legend-missing"><i></i>Missing {{ coverageProgress.missing }}</span>
      </div>
    </section>

    <section class="content-job-panel" aria-labelledby="content-job-title">
      <div class="content-job-head">
        <div>
          <span class="eyebrow">Generation job</span>
          <h5 id="content-job-title">Generate part descriptions</h5>
          <p>Preview the eligible scope, run generation, and monitor progress without leaving this page.</p>
        </div>
        <span class="live-state" :class="{ connected: jobWsConnected }">
          <span aria-hidden="true"></span>{{ jobWsConnected ? 'Live' : 'Reconnecting...' }}
        </span>
      </div>

      <div class="job-controls">
        <label>
          <span>Category</span>
          <select v-model="jobOptions.type" class="job-control-input" :disabled="jobRunBusy" @change="queueJobScope">
            <option v-for="option in typeOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
          </select>
        </label>
        <label>
          <span>Limit</span>
          <input
            v-model.number="jobOptions.limit"
            class="job-control-input job-limit"
            type="number"
            min="1"
            max="1000"
            :disabled="jobRunBusy"
            @change="queueJobScope"
          >
        </label>
        <button class="gc-act subtle" type="button" :disabled="jobScopeLoading" @click="fetchJobScope">
          {{ jobScopeLoading ? 'Checking...' : 'Refresh scope' }}
        </button>
        <button class="gc-act primary" type="button" :disabled="!canRunContentJob" @click="runContentJob">
          <span v-if="jobRunBusy" class="inline-spinner" aria-hidden="true"></span>
          {{ jobRunBusy ? 'Starting...' : 'Run generation' }}
        </button>
        <button class="gc-act subtle" type="button" :disabled="!!jobCleanupBusy" @click="cleanupJobs('stale')">
          {{ jobCleanupBusy === 'stale' ? 'Stopping...' : 'Stop stuck' }}
        </button>
        <button class="gc-act danger-soft" type="button" :disabled="!!jobCleanupBusy" @click="cleanupJobs('all')">
          {{ jobCleanupBusy === 'all' ? 'Stopping...' : 'Stop all running' }}
        </button>
      </div>

      <div v-if="jobScopeError" class="job-inline-error">{{ jobScopeError }}</div>
      <div v-else-if="jobScope" class="scope-summary">
        <strong>{{ scopeEligibleTotal }} eligible</strong>
        <span>{{ scopeTotal }} total in scope</span>
        <span v-for="chip in scopeCountChips" :key="chip.label" class="scope-chip">
          {{ chip.label }} {{ chip.value }}
        </span>
      </div>
      <details v-if="scopeItems.length" class="scope-items">
        <summary>Preview {{ scopeItems.length }} scoped {{ scopeItems.length === 1 ? 'part' : 'parts' }}</summary>
        <ul>
          <li v-for="(item, index) in scopeItems" :key="rowIdentity(item) || index">
            <span>{{ prettyType(rowType(item)) }}</span>
            <strong>{{ partName(item) }}</strong>
            <small>#{{ partId(item) }}</small>
          </li>
        </ul>
      </details>

      <div v-if="trackedJobRun" class="content-job-progress">
        <div class="job-progress-copy">
          <strong>{{ jobRunStatusLabel }}</strong>
          <span v-if="jobRunTriggerLabel">{{ jobRunTriggerLabel }} run</span>
          <span v-if="jobRunId">Run #{{ jobRunId }}</span>
          <span>{{ jobRunProgress }}%</span>
        </div>
        <div class="progress-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" :aria-valuenow="jobRunProgress">
          <div class="progress-fill" :style="{ width: jobRunProgress + '%' }"></div>
        </div>
        <details v-if="jobLogs.length" class="job-log-panel">
          <summary>{{ jobLogs.length }} live log {{ jobLogs.length === 1 ? 'entry' : 'entries' }}</summary>
          <ol>
            <li v-for="(entry, index) in jobLogs" :key="index">{{ entry }}</li>
          </ol>
        </details>
      </div>
    </section>

    <div class="tabs-row">
      <div class="tabs">
        <button
          v-for="tab in statusTabs"
          :key="tab.value"
          class="tab"
          :class="{ active: currentStatus === tab.value }"
          type="button"
          @click="selectStatus(tab.value)"
        >
          {{ tab.label }} <span class="tab-count">{{ tab.count }}</span>
        </button>
      </div>
    </div>

    <section class="filter-panel" aria-label="Filter content">
      <div class="filter-panel-head">
        <div>
          <span class="filter-title">Filter parts</span>
          <span class="filter-summary">{{ filteredMeta }}</span>
        </div>
        <button
          v-if="hasActiveFilters"
          class="clear-filters"
          type="button"
          :disabled="loading"
          @click="resetFilters"
        >Clear filters</button>
      </div>
      <div class="filters content-filters">
        <b-form-input
          v-model.trim="filters.q"
          class="hfilter grow content-search"
          type="search"
          placeholder="Search by model"
          aria-label="Search content by model"
          @input="debouncedFilterChange"
          @keyup.enter="onFilterChange"
        />
        <b-dropdown
          class="gc-admin-dropdown content-filter-dropdown"
          toggle-class="gc-admin-dropdown-toggle"
          menu-class="gc-admin-dropdown-menu"
          variant="link"
          no-caret
          aria-label="Filter by category"
        >
          <template v-slot:button-content>
            <span>{{ selectedOptionLabel(typeOptions, filters.type) }}</span>
            <span class="gc-admin-dropdown-caret" aria-hidden="true">v</span>
          </template>
          <b-dropdown-item-button
            v-for="option in typeOptions"
            :key="option.value"
            :class="{ active: option.value === filters.type }"
            @click="setTypeFilter(option.value)"
          >
            {{ option.text }}
          </b-dropdown-item-button>
        </b-dropdown>
        <b-dropdown
          class="gc-admin-dropdown content-filter-dropdown"
          toggle-class="gc-admin-dropdown-toggle"
          menu-class="gc-admin-dropdown-menu"
          variant="link"
          no-caret
          aria-label="Filter by content status"
        >
          <template v-slot:button-content>
            <span>{{ selectedOptionLabel(statusOptions, filters.status) }}</span>
            <span class="gc-admin-dropdown-caret" aria-hidden="true">v</span>
          </template>
          <b-dropdown-item-button
            v-for="option in statusOptions"
            :key="option.value"
            :class="{ active: option.value === filters.status }"
            @click="setStatusFilter(option.value)"
          >
            {{ option.text }}
          </b-dropdown-item-button>
        </b-dropdown>
      </div>
    </section>

    <div class="content-toolbar">
      <div class="selection-controls">
        <label class="select-filtered">
          <input
            type="checkbox"
            :checked="allVisibleSelected"
            :indeterminate.prop="someVisibleSelected"
            :disabled="!rows.length"
            aria-label="Select all currently loaded filtered parts"
            @change="toggleVisibleSelection($event.target.checked)"
          >
          <span>Select loaded ({{ rows.length }})</span>
        </label>
        <button
          class="gc-act subtle"
          type="button"
          :disabled="!rows.length || selectingFiltered"
          @click="selectAllFiltered"
        >
          {{ selectingFiltered ? 'Selecting...' : 'Select all filtered (' + filteredSelectCount + ')' }}
        </button>
      </div>

      <div class="bulk-actions" role="region" aria-label="Bulk actions" aria-live="polite">
        <strong :class="{ muted: !selectedCount }">
          {{ selectedCount ? selectedCount + ' selected' : 'No parts selected' }}
        </strong>
        <button
          class="gc-act primary"
          type="button"
          :disabled="!selectedCount || generating || approving"
          @click="researchSelected"
        >
          {{ generating ? 'Regenerating...' : 'Regenerate selected' }}
        </button>
        <button
          class="gc-act archive"
          type="button"
          :disabled="!canApproveSelected || generating || approving"
          :title="approveDisabledTitle"
          @click="approveSelected"
        >
          {{ approving ? 'Approving...' : 'Approve selected' }}
        </button>
        <button
          class="gc-act subtle"
          type="button"
          :disabled="!selectedCount || generating || approving"
          @click="clearSelection"
        >Clear</button>
      </div>
    </div>

    <div class="catalog-table-section content-table-section">
      <LoadingOverlay
        v-if="loading && !rows.length"
        mode="block"
        compact
        min-height="260px"
      />

      <div v-else class="gc-table-shell content-table-shell" :class="{ 'is-loading': loading }">
        <LoadingOverlay v-if="loading && rows.length" compact />
        <div class="gc-table-scroll">
          <table class="gc-admin-table content-table">
            <thead>
              <tr>
                <th class="gc-table-head select-col">Select</th>
                <th class="gc-table-head part-col">Part</th>
                <th class="gc-table-head fields-col">Content fields</th>
                <th class="gc-table-head complete-col">Completeness</th>
                <th class="gc-table-head status-col">Status</th>
                <th class="gc-table-head actions-col"></th>
              </tr>
            </thead>

            <tbody v-if="!groupedRows.length">
              <tr>
                <td colspan="6" class="gc-table-empty">
                  No parts match these filters.
                </td>
              </tr>
            </tbody>

            <tbody
              v-for="group in groupedRows"
              :key="group.type"
              class="content-group"
            >
              <tr class="content-group-row">
                <td colspan="6" class="gc-table-cell">
                  <div class="group-head">
                    <button
                      class="group-toggle"
                      type="button"
                      :aria-expanded="isGroupOpen(group.type) ? 'true' : 'false'"
                      @click="toggleGroup(group.type)"
                    >
                      <span aria-hidden="true">{{ isGroupOpen(group.type) ? 'v' : '>' }}</span>
                      <span>{{ prettyType(group.type) }}</span>
                    </button>
                    <label class="group-select">
                      <input
                        type="checkbox"
                        :checked="groupAllSelected(group)"
                        :indeterminate.prop="groupSomeSelected(group)"
                        :aria-label="'Select all ' + prettyType(group.type) + ' rows'"
                        @change="toggleGroupSelection(group, $event.target.checked)"
                      >
                      <span>{{ group.rows.length }} parts</span>
                    </label>
                    <div class="group-complete">
                      <span>{{ group.complete }} / {{ group.rows.length }} complete</span>
                      <div class="mini-progress" aria-hidden="true">
                        <div
                          class="mini-progress-fill"
                          :style="{ width: group.percent + '%' }"
                        ></div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>

              <template v-if="isGroupOpen(group.type)">
                <template v-for="row in group.rows">
                  <tr
                    :key="rowKey(row)"
                    class="gc-table-row content-row"
                    :class="{ selected: isSelected(row), pending: isPending(row) }"
                    @click="openReview(row)"
                  >
                    <td class="gc-table-cell select-col" @click.stop>
                      <input
                        type="checkbox"
                        :checked="isSelected(row)"
                        :aria-label="'Select ' + partName(row)"
                        @click.stop
                        @change="toggleRowSelection(row, $event.target.checked)"
                      >
                    </td>
                    <td class="gc-table-cell part-col">
                      <div class="part-cell">
                        <button
                          type="button"
                          class="part-thumb"
                          :class="{ 'is-empty': !rowImage(row) || row._imageFailed }"
                          :aria-label="'Review content for ' + partName(row)"
                          @click.stop="openReview(row)"
                        >
                          <img
                            v-if="rowImage(row) && !row._imageFailed"
                            :src="rowImage(row)"
                            :alt="partName(row)"
                            @error="markImageFailed(row)"
                          >
                          <span v-else class="part-avatar" aria-hidden="true">{{ partInitials(row) }}</span>
                        </button>
                        <div class="part-copy">
                          <button
                            type="button"
                            class="part-link"
                            @click.stop="openReview(row)"
                          >{{ partName(row) }}</button>
                          <span class="muted-sub">#{{ partId(row) }} - {{ prettyType(rowType(row)) }} - {{ availabilityLabel(row) }}</span>
                          <span v-if="rowMeta(row)" class="part-meta-line">{{ rowMeta(row) }}</span>
                        </div>
                      </div>
                    </td>
                    <td class="gc-table-cell fields-col">
                      <div class="field-dot-list desktop-fields" role="list" :aria-label="fieldSummary(row)">
                        <span
                          v-for="field in contentFields"
                          :key="field.key"
                          class="field-token"
                          :class="{ present: fieldPresent(row, field.key), missing: !fieldPresent(row, field.key), unverified: isUnverifiedField(row, field.key) }"
                          role="listitem"
                          :aria-label="fieldTooltip(row, field)"
                          :title="fieldTooltip(row, field)"
                          tabindex="0"
                        >
                          <span class="field-dot" aria-hidden="true"></span>
                          <span class="field-label">{{ field.short }}</span>
                          <span v-if="fieldInlineValue(row, field.key)" class="field-value">
                            {{ fieldInlineValue(row, field.key) }}
                          </span>
                        </span>
                      </div>
                      <div class="mobile-fields">
                        <div class="meter-wrap">
                          <div class="progress-track compact">
                            <div
                              class="progress-fill"
                              :style="{ width: completenessPercent(row) + '%' }"
                            ></div>
                          </div>
                          <span>{{ completenessText(row) }}</span>
                        </div>
                        <button
                          class="detail-toggle"
                          type="button"
                          :aria-expanded="isRowExpanded(row) ? 'true' : 'false'"
                          @click.stop="toggleRowDetails(row)"
                        >
                          {{ isRowExpanded(row) ? 'Hide fields' : 'Show fields' }}
                        </button>
                      </div>
                    </td>
                    <td class="gc-table-cell complete-col">
                      <div class="meter-wrap">
                        <div class="progress-track compact" :aria-label="completenessText(row)">
                          <div
                            class="progress-fill"
                            :style="{ width: completenessPercent(row) + '%' }"
                          ></div>
                        </div>
                        <span>{{ completenessText(row) }}</span>
                      </div>
                    </td>
                    <td class="gc-table-cell status-col">
                      <div class="status-stack">
                        <span class="content-status" :class="statusClass(row)">
                          <span v-if="isPending(row)" class="inline-spinner" aria-hidden="true"></span>
                          {{ statusLabel(row) }}
                        </span>
                        <span
                          v-if="unverifiedList(row).length"
                          class="unverified-hint"
                          :title="unverifiedList(row).join(', ')"
                        >
                          ! {{ unverifiedList(row).length }} unverified
                        </span>
                      </div>
                    </td>
                    <td class="gc-table-cell actions-col" @click.stop>
                      <AdminActionMenu :disabled="isRowBusy(row)">
                        <button
                          class="row-btn review"
                          type="button"
                          :disabled="isRowBusy(row)"
                          @click.stop="openReview(row)"
                        >Review</button>
                        <button
                          class="row-btn"
                          type="button"
                          :disabled="isRowBusy(row)"
                          @click.stop="regenerateRow(row)"
                        >
                          <span v-if="isRowBusy(row)" class="inline-spinner" aria-hidden="true"></span>
                          {{ rowBusyLabel(row, 'Regenerate') }}
                        </button>
                      </AdminActionMenu>
                    </td>
                  </tr>
                  <tr
                    v-if="isRowExpanded(row)"
                    :key="rowKey(row) + '-details'"
                    class="mobile-detail-row"
                  >
                    <td colspan="6" class="gc-table-cell">
                      <div class="mobile-field-grid">
                        <div
                          v-for="field in contentFields"
                          :key="field.key"
                          class="mobile-field-item"
                          :class="{ present: fieldPresent(row, field.key), unverified: isUnverifiedField(row, field.key) }"
                        >
                          <span>{{ field.label }}</span>
                          <strong>{{ fieldDetailText(row, field) }}</strong>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="hasPagination" class="pager-row">
      <button class="gc-act subtle" type="button" :disabled="page <= 1 || loading" @click="setPage(page - 1)">Previous</button>
      <span>Page {{ page }} of {{ totalPages }}</span>
      <button class="gc-act subtle" type="button" :disabled="page >= totalPages || loading" @click="setPage(page + 1)">Next</button>
    </div>

    <b-modal
      id="content-review-modal"
      v-model="reviewModalOpen"
      size="lg"
      centered
      hide-footer
      title="Review generated content"
      @hidden="clearReview"
    >
      <div v-if="reviewLoading" class="review-loading">
        <LoadingOverlay mode="block" compact clear min-height="220px" />
      </div>
      <div v-else-if="reviewError" class="panel-error">{{ reviewError }}</div>
      <div v-else-if="reviewItem" class="review-content">
        <div class="review-head">
          <div>
            <span class="eyebrow">{{ prettyType(rowType(reviewSourceRow)) }}</span>
            <h5>{{ partName(reviewSourceRow) }}</h5>
          </div>
          <div class="review-meta">
            <span class="content-status" :class="statusClass(reviewSourceRow)">
              {{ statusLabel(reviewSourceRow) }}
            </span>
            <span>Confidence {{ confidenceLabel(reviewConfidence) }}</span>
          </div>
        </div>

        <!-- TODO(edit): add inline editors here after the v1 read-only review flow. -->
        <div class="review-grid">
          <section
            v-for="field in reviewFields"
            :key="field.key"
            class="review-field"
            :class="{ unverified: isReviewFieldUnverified(field.key) }"
          >
            <span class="review-label">{{ field.label }}</span>

            <a
              v-if="field.key === 'officialUrl' && reviewFieldValue(field.key)"
              :href="reviewFieldValue(field.key)"
              class="review-link"
              target="_blank"
              rel="noopener"
            >{{ reviewFieldValue(field.key) }}</a>

            <div v-else-if="field.key === 'bestFor'" class="chip-list">
              <span
                v-for="chip in bestForChips"
                :key="chip"
                class="issue-chip"
              >{{ chip }}</span>
              <span v-if="!bestForChips.length" class="muted-sub">No value</span>
            </div>

            <p v-else class="review-value">{{ formattedReviewValue(field.key) }}</p>
          </section>
        </div>

        <section class="review-sources">
          <span class="review-label">Sources</span>
          <ul v-if="reviewSources.length">
            <li v-for="(source, index) in reviewSources" :key="sourceKey(source, index)">
              <a
                v-if="sourceUrl(source)"
                :href="sourceUrl(source)"
                target="_blank"
                rel="noopener"
              >{{ sourceTitle(source, index) }}</a>
              <span v-else>{{ sourceTitle(source, index) }}</span>
            </li>
          </ul>
          <span v-else class="muted-sub">No sources returned.</span>
        </section>

        <div v-if="reviewUnverified.length" class="review-unverified">
          <span class="review-label">Unverified fields</span>
          <div class="chip-list">
            <span v-for="field in reviewUnverified" :key="field" class="issue-chip warn">{{ prettyLabel(field) }}</span>
          </div>
        </div>

        <div class="review-actions">
          <button
            class="gc-act archive"
            type="button"
            :disabled="reviewBusy || !canApproveReview"
            @click="approveReview"
          >
            {{ reviewBusy === 'approve' ? 'Approving...' : 'Approve' }}
          </button>
          <button
            class="gc-act primary"
            type="button"
            :disabled="!!reviewBusy || isRowBusy(reviewSourceRow)"
            @click="regenerateReview"
          >
            <span v-if="reviewBusy === 'regenerate'" class="inline-spinner" aria-hidden="true"></span>
            {{ reviewBusy === 'regenerate' ? 'Regenerating...' : 'Regenerate' }}
          </button>
          <button class="gc-act subtle" type="button" :disabled="reviewBusy" @click="reviewModalOpen = false">
            Close
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain } from '../../config'
import realtime from '../../services/realtime'
import LoadingOverlay from '../widgets/LoadingOverlay.vue'
import AdminActionMenu from './shared/AdminActionMenu.vue'

const PART_TYPES = ['all', 'cpu', 'motherboard', 'ram', 'storage', 'gpu', 'psu', 'case']
const CONTENT_JOB_KEY = 'generate-part-descriptions'
const GENERATE_TIMEOUT_MS = 120000

const CONTENT_FIELDS = [
  { key: 'originalDescription', label: 'Description', short: 'Desc' },
  { key: 'seoSummary', label: 'SEO summary', short: 'SEO' },
  { key: 'tier', label: 'Tier', short: 'Tier' },
  { key: 'bestFor', label: 'Best for', short: 'Best' },
  { key: 'uniqueNote', label: 'Unique note', short: 'Note' },
  { key: 'releasedYear', label: 'Released year', short: 'Year' },
  { key: 'releasedDate', label: 'Released date', short: 'Date' },
  { key: 'discontinued', label: 'Discontinued', short: 'Disc' },
  { key: 'discontinuedYear', label: 'Discontinued year', short: 'Disc year' },
  { key: 'officialUrl', label: 'Official URL', short: 'URL' }
]

export default {
  name: 'ContentManagement',
  components: {
    AdminActionMenu,
    LoadingOverlay
  },
  data () {
    return {
      rows: [],
      counts: {},
      filters: {
        q: '',
        type: 'all',
        status: 'all'
      },
      page: 1,
      perPage: 50,
      pagination: {
        total: 0,
        filtered: 0,
        perPage: 50,
        totalPages: 1
      },
      selectedIds: {},
      loadedRowsById: {},
      completedRowsCache: [],
      groupOpen: {},
      rowDetailsOpen: {},
      busy: {},
      loading: false,
      generating: false,
      approving: false,
      selectingFiltered: false,
      error: null,
      notice: null,
      searchTimer: null,
      reviewModalOpen: false,
      reviewLoading: false,
      reviewError: '',
      reviewItem: null,
      reviewSourceRow: null,
      reviewBusy: '',
      jobOptions: {
        type: 'all',
        limit: 50
      },
      jobScope: null,
      jobScopeLoading: false,
      jobScopeError: '',
      jobScopeTimer: null,
      jobRunBusy: false,
      jobCleanupBusy: '',
      trackedJobRun: null,
      jobLogs: [],
      unsubscribeJobLive: null,
      unsubscribeContentLive: null,
      unsubscribeLiveState: null,
      jobWsConnected: false,
      jobPollTimer: null,
      contentSnapshotTimer: null,
      partTypes: PART_TYPES,
      contentFields: CONTENT_FIELDS
    }
  },
  computed: {
    currentStatus () {
      return this.filters.status || 'all'
    },
    hasActiveFilters () {
      return !!this.filters.q || this.filters.type !== 'all' || this.currentStatus !== 'all'
    },
    typeOptions () {
      return this.partTypes.map((type) => {
        return {
          value: type,
          text: type === 'all' ? 'All categories' : this.prettyType(type)
        }
      })
    },
    statusOptions () {
      return [
        { value: 'all', text: 'All statuses' },
        { value: 'empty', text: 'Empty' },
        { value: 'pending', text: 'Pending' },
        { value: 'needs_review', text: 'Needs review' },
        { value: 'completed', text: 'Completed' },
        { value: 'failed', text: 'Failed' }
      ]
    },
    statusTabs () {
      return [
        { value: 'all', label: 'All', count: this.kpis.total },
        { value: 'empty', label: 'Missing', count: this.kpis.missing },
        { value: 'needs_review', label: 'Needs review', count: this.kpis.needsReview },
        { value: 'failed', label: 'Failed', count: this.kpis.failed },
        { value: 'completed', label: 'Completed', count: this.kpis.completed }
      ]
    },
    kpis () {
      return {
        total: this.totalCount(),
        missing: this.statusCount('empty'),
        needsReview: this.statusCount('needs_review'),
        failed: this.statusCount('failed'),
        generated: this.statusCount('generated'),
        approved: this.statusCount('approved'),
        completed: this.statusCount('generated') + this.statusCount('approved')
      }
    },
    coverageProgress () {
      const total = Math.max(0, Number(this.kpis.total) || 0)
      const completed = Math.min(total, Math.max(0, Number(this.kpis.completed) || 0))
      const failed = Math.min(total - completed, Math.max(0, Number(this.kpis.failed) || 0))
      const missing = Math.min(total - completed - failed, Math.max(0, Number(this.kpis.missing) || 0))
      const inProgress = Math.max(0, total - completed - failed - missing)
      const width = (value) => total ? (value / total) * 100 : 0
      return {
        total,
        completed,
        failed,
        missing,
        inProgress,
        percent: total ? Math.round((completed / total) * 100) : 0,
        completedWidth: width(completed),
        inProgressWidth: width(inProgress),
        failedWidth: width(failed)
      }
    },
    groupedRows () {
      const buckets = {}
      this.rows.forEach((row) => {
        const type = this.rowType(row) || 'other'
        if (!buckets[type]) buckets[type] = []
        buckets[type].push(row)
      })

      const ordered = this.partTypes
        .filter((type) => type !== 'all' && buckets[type])
        .concat(Object.keys(buckets).filter((type) => this.partTypes.indexOf(type) === -1).sort())

      return ordered.map((type) => {
        const rows = buckets[type]
        const complete = rows.filter((row) => this.isContentComplete(row)).length
        const percent = rows.length ? Math.round((complete / rows.length) * 100) : 0
        return { type, rows, complete, percent }
      })
    },
    selectedIdsArray () {
      return Object.keys(this.selectedIds).filter((id) => this.selectedIds[id])
    },
    selectedCount () {
      return this.selectedIdsArray.length
    },
    selectedRows () {
      return this.selectedIdsArray
        .map((id) => this.loadedRowsById[id])
        .filter((row) => !!row)
    },
    canApproveSelected () {
      return this.selectedCount > 0 &&
        this.selectedRows.length === this.selectedCount &&
        this.selectedRows.every((row) => this.canApproveRow(row))
    },
    approveDisabledTitle () {
      if (this.canApproveSelected) return ''
      return 'Only generated or needs review rows can be approved.'
    },
    allVisibleSelected () {
      return this.rows.length > 0 && this.rows.every((row) => this.isSelected(row))
    },
    someVisibleSelected () {
      return this.rows.some((row) => this.isSelected(row)) && !this.allVisibleSelected
    },
    filteredMeta () {
      if (!this.rows.length) return '0 loaded'
      const total = Number(this.pagination.filtered) || Number(this.pagination.total) || this.rows.length
      if (total === this.rows.length) return `${this.rows.length} loaded`
      return `${this.rows.length} loaded of ${total}`
    },
    filteredSelectCount () {
      return Number(this.pagination.filtered) || Number(this.pagination.total) || this.rows.length
    },
    hasPagination () {
      return this.totalPages > 1
    },
    totalPages () {
      return Math.max(1, Number(this.pagination.totalPages) || 1)
    },
    reviewFields () {
      return CONTENT_FIELDS
    },
    reviewContent () {
      const item = this.reviewItem || {}
      return this.unwrap(item, ['content', 'generatedContent', 'generated', 'fields']) || item
    },
    reviewUnverified () {
      const item = this.reviewItem || {}
      const content = this.reviewContent || {}
      return this.normalizeArray(
        this.pick(item, ['unverified', 'unverifiedFields'],
          this.pick(content, ['unverified', 'unverifiedFields'],
            this.reviewSourceRow ? this.unverifiedList(this.reviewSourceRow) : []))
      )
    },
    reviewConfidence () {
      const item = this.reviewItem || {}
      const content = this.reviewContent || {}
      return this.pick(item, ['confidence', 'score'],
        this.pick(content, ['confidence', 'score'],
          this.reviewSourceRow ? this.reviewSourceRow.confidence : null))
    },
    bestForChips () {
      const value = this.reviewFieldValue('bestFor')
      if (Array.isArray(value)) return value.filter((entry) => !!entry)
      if (!value) return []
      return String(value).split(/[,;|]/).map((entry) => entry.trim()).filter((entry) => !!entry)
    },
    reviewSources () {
      const item = this.reviewItem || {}
      const content = this.reviewContent || {}
      return this.normalizeArray(
        this.pick(item, ['sources', 'sourceLinks', 'citations'],
          this.pick(content, ['sources', 'sourceLinks', 'citations'], []))
      )
    },
    canApproveReview () {
      return this.reviewSourceRow && this.canApproveRow(this.reviewSourceRow)
    },
    scopeTotal () {
      return Number(this.pick(this.jobScope, ['total'], 0)) || 0
    },
    scopeEligibleTotal () {
      return Number(this.pick(this.jobScope, ['eligibleTotal', 'eligible_total'], 0)) || 0
    },
    scopeCountChips () {
      const counts = this.pick(this.jobScope, ['counts'], {}) || {}
      return Object.keys(counts)
        .filter((key) => counts[key] !== undefined && counts[key] !== null)
        .map((key) => ({ label: this.prettyLabel(key), value: counts[key] }))
    },
    scopeItems () {
      return this.asList(this.jobScope, ['items'])
    },
    canRunContentJob () {
      return !this.jobRunBusy && !this.isTrackedJobActive && this.scopeEligibleTotal > 0
    },
    isTrackedJobActive () {
      return this.runIsActive(this.trackedJobRun)
    },
    jobRunId () {
      return this.pick(this.trackedJobRun, ['id', 'runId', 'run_id'], '')
    },
    jobRunStatusLabel () {
      return this.prettyLabel(this.pick(this.trackedJobRun, ['status', 'state'], 'Starting'))
    },
    jobRunTriggerLabel () {
      return this.prettyLabel(this.pick(this.trackedJobRun, ['trigger'], ''))
    },
    jobRunProgress () {
      const run = this.trackedJobRun || {}
      const stats = this.pick(run, ['stats', 'progressStats'], run) || run
      const current = this.numOrNull(this.pick(stats, ['current', 'processed', 'completed', 'done'], null))
      const total = this.numOrNull(this.pick(stats, ['total', 'eligibleTotal'], null))
      const direct = this.numOrNull(this.pick(run, ['progress', 'progressPercent', 'percent'], null))
      if (run._progressSource === 'direct' && direct !== null) {
        return Math.max(0, Math.min(100, Math.round(direct)))
      }
      if (current !== null && total) return Math.max(0, Math.min(100, Math.round((current / total) * 100)))
      if (direct !== null) return Math.max(0, Math.min(100, Math.round(direct)))
      return this.isTrackedJobActive ? 2 : 100
    }
  },
  mounted () {
    this.fetchContent()
    this.fetchJobScope()
    this.connectJobsSocket()
    this.discoverActiveContentJob()
  },
  beforeDestroy () {
    this.stopJobPolling()
    this.disconnectJobsSocket()
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
      this.searchTimer = null
    }
    if (this.jobScopeTimer) {
      clearTimeout(this.jobScopeTimer)
      this.jobScopeTimer = null
    }
    if (this.contentSnapshotTimer) {
      clearTimeout(this.contentSnapshotTimer)
      this.contentSnapshotTimer = null
    }
  },
  methods: {
    async refreshContentDashboard () {
      await this.fetchContent()
    },
    authHeaders () {
      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.usertoken}`
      }
    },
    websocketBaseUrl () {
      const url = new URL(apiDomain, window.location.origin)
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
      url.pathname = url.pathname.replace(/\/api\/?$/, '') || '/'
      url.search = ''
      url.hash = ''
      return url.toString().replace(/\/$/, '')
    },
    connectJobsSocket () {
      if (this.unsubscribeJobLive || this.unsubscribeContentLive) return
      this.jobWsConnected = realtime.isConnected()
      this.unsubscribeLiveState = realtime.onStateChange((state, connected) => {
        this.jobWsConnected = !!connected
        if (connected && state === 'open') {
          this.stopJobPolling()
          realtime.refresh('jobs:all')
          realtime.emit('content:all', 'onRefresh', {})
        } else if (this.isTrackedJobActive) {
          this.scheduleJobPoll()
        }
      })
      this.subscribeJobsChannel()
      this.subscribeContentChannel()
    },
    disconnectJobsSocket () {
      if (this.unsubscribeContentLive) this.unsubscribeContentLive()
      if (this.unsubscribeJobLive) this.unsubscribeJobLive()
      if (this.unsubscribeLiveState) this.unsubscribeLiveState()
      this.unsubscribeContentLive = null
      this.unsubscribeJobLive = null
      this.unsubscribeLiveState = null
      this.jobWsConnected = false
    },
    subscribeJobsChannel () {
      this.unsubscribeJobLive = realtime.subscribe('jobs:all', {
        'job:snapshot': this.handleJobSnapshot,
        'job:started': (payload) => this.handleJobEvent('started', payload),
        'job:progress': (payload) => this.handleJobEvent('progress', payload),
        'job:log': (payload) => this.handleJobEvent('log', payload),
        'job:finished': (payload) => this.handleJobEvent('finished', payload),
        'job:error': (payload) => this.handleJobEvent('error', payload)
      })
    },
    subscribeContentChannel () {
      this.unsubscribeContentLive = realtime.subscribe('content:all', {
        'content:snapshot': this.handleContentSnapshot,
        'content:updated': this.handleContentUpdated,
        'content:bulk': this.handleContentBulk
      })
    },
    handleContentSnapshot (payload) {
      if (!payload || !payload.counts) return
      this.counts = this.normalizeCounts(payload)
    },
    handleContentUpdated (payload) {
      const row = this.unwrap(payload, ['row', 'data']) || payload
      const id = this.rowIdentity(row)
      if (!id) return
      this.replaceRow(id, row)
      this.ensureGroupState([row])
      if (this.reviewModalOpen && this.reviewSourceRow && this.rowIdentity(this.reviewSourceRow) === id) {
        this.reviewItem = row
      }
      this.queueContentSnapshot()
    },
    handleContentBulk (payload) {
      const type = this.catalogPartType(this.pick(payload, ['type'], ''))
      this.queueContentSnapshot()
      if (!type || type === 'all' || this.filters.type === 'all' || this.filters.type === type) {
        this.fetchContent()
      }
    },
    queueContentSnapshot () {
      if (this.contentSnapshotTimer) clearTimeout(this.contentSnapshotTimer)
      this.contentSnapshotTimer = setTimeout(() => {
        this.contentSnapshotTimer = null
        this.requestContentSnapshot()
      }, 250)
    },
    requestContentSnapshot () {
      realtime.emit('content:all', 'onRefresh', {})
    },
    handleJobSnapshot (payload) {
      const runs = this.jobRunsFromPayload(payload)
      if (runs.length) {
        const activeRun = runs.find((run) => this.isContentJobRun(run) && this.runIsActive(run))
        if (activeRun) this.setTrackedJobRun(activeRun)
        return
      }
      this.handleJobEvent('snapshot', payload)
    },
    handleJobEvent (event, payload) {
      const outer = payload || {}
      const run = this.unwrap(outer, ['run', 'data']) || outer
      const jobKey = this.pick(run, ['jobKey', 'job_key', 'job', 'key'],
        this.pick(outer, ['jobKey', 'job_key', 'job', 'key'], ''))
      if (jobKey !== CONTENT_JOB_KEY) return

      const statusByEvent = {
        started: 'running',
        progress: 'running',
        finished: this.pick(run, ['status', 'state'], 'finished'),
        error: this.pick(run, ['status', 'state'], 'failed')
      }
      const wasActive = this.isTrackedJobActive
      this.setTrackedJobRun(Object.assign({}, run, {
        jobKey: CONTENT_JOB_KEY,
        status: statusByEvent[event] || this.pick(run, ['status', 'state'], 'running')
      }))

      if (event === 'log') this.appendJobLog(outer)
      if (!this.isTrackedJobActive && (wasActive || event === 'finished' || event === 'error')) {
        this.stopJobPolling()
        this.fetchContent()
        this.fetchJobScope()
      }
    },
    async discoverActiveContentJob () {
      try {
        const { data } = await axios.get(
          `${apiDomain}jobs/runs?limit=25`,
          { headers: this.authHeaders() }
        )
        const runs = this.jobRunsFromPayload(data)
        const activeRun = runs.find((run) => this.isContentJobRun(run) && this.runIsActive(run))
        if (!activeRun) return
        this.setTrackedJobRun(activeRun)
        if (!this.jobWsConnected) this.scheduleJobPoll()
      } catch (e) {
        // Live events remain the primary source if run discovery is unavailable.
      }
    },
    isContentJobRun (run) {
      return this.pick(run, ['jobKey', 'job_key', 'job', 'key'], '') === CONTENT_JOB_KEY
    },
    runIsActive (run) {
      const status = String(this.pick(run, ['status', 'state'], '')).toLowerCase()
      return ['starting', 'queued', 'pending', 'running', 'active'].indexOf(status) !== -1
    },
    setTrackedJobRun (run) {
      const currentId = this.pick(this.trackedJobRun, ['id', 'runId', 'run_id'], '')
      const nextId = this.pick(run, ['id', 'runId', 'run_id'], '')
      const sameRun = !currentId || !nextId || String(currentId) === String(nextId)
      if (!sameRun) this.jobLogs = []
      const previous = sameRun ? (this.trackedJobRun || {}) : {}
      const next = Object.assign({}, previous, run)
      const incomingStats = this.pick(run, ['stats', 'progressStats'], null)
      if (incomingStats) {
        next.stats = Object.assign({}, this.pick(previous, ['stats', 'progressStats'], {}) || {}, incomingStats)
        next._progressSource = 'stats'
      } else if (['progress', 'progressPercent', 'percent'].some((key) => Object.prototype.hasOwnProperty.call(run, key))) {
        next._progressSource = 'direct'
      }
      this.trackedJobRun = next
      const log = this.pick(run, ['log'], '')
      if (log && !this.jobLogs.length) {
        this.jobLogs = String(log).split(/\r?\n/).filter((entry) => !!entry).slice(-100)
      }
    },
    jobRunsFromPayload (payload) {
      if (Array.isArray(payload)) return payload
      const direct = this.asList(payload, ['runs', 'items'])
      if (direct.length) return direct
      const nested = this.unwrap(payload, ['data'])
      if (Array.isArray(nested)) return nested
      return this.asList(nested, ['runs', 'items'])
    },
    appendJobLog (payload) {
      const data = this.unwrap(payload, ['data']) || payload || {}
      const message = typeof data === 'string'
        ? data
        : this.pick(data, ['message', 'log', 'text'], '')
      if (!message) return
      this.jobLogs = this.jobLogs.concat(String(message)).slice(-100)
    },
    queueJobScope () {
      if (this.jobScopeTimer) clearTimeout(this.jobScopeTimer)
      this.jobScopeTimer = setTimeout(() => {
        this.jobScopeTimer = null
        this.fetchJobScope()
      }, 250)
    },
    async fetchJobScope () {
      this.jobScopeLoading = true
      this.jobScopeError = ''
      try {
        const params = new URLSearchParams()
        params.set('type', this.jobOptions.type || 'all')
        params.set('limit', String(Math.max(1, Number(this.jobOptions.limit) || 50)))
        const { data } = await axios.get(
          `${apiDomain}jobs/${CONTENT_JOB_KEY}/scope?${params.toString()}`,
          { headers: this.authHeaders() }
        )
        this.jobScope = this.unwrap(data, ['scope', 'data']) || data || {}
      } catch (e) {
        this.jobScope = null
        this.jobScopeError = this.errMsg(e)
      } finally {
        this.jobScopeLoading = false
      }
    },
    async runContentJob () {
      if (!this.canRunContentJob) return
      const options = {
        type: this.jobOptions.type || 'all',
        limit: Math.max(1, Number(this.jobOptions.limit) || 50)
      }
      this.jobRunBusy = true
      this.jobLogs = []
      this.trackedJobRun = {
        jobKey: CONTENT_JOB_KEY,
        status: 'starting',
        startedAt: new Date().toISOString(),
        stats: { current: 0, total: this.scopeEligibleTotal }
      }
      try {
        const { data } = await axios.post(
          `${apiDomain}jobs/${CONTENT_JOB_KEY}/run`,
          { options },
          { headers: this.authHeaders(), timeout: 30000 }
        )
        const run = this.unwrap(data, ['run', 'data']) || data || {}
        this.setTrackedJobRun(Object.assign({}, run, { jobKey: CONTENT_JOB_KEY }))
        this.notice = `Content generation started${this.jobRunId ? ` as run #${this.jobRunId}` : ''}.`
        this.error = null
        if (!this.jobWsConnected) this.scheduleJobPoll()
      } catch (e) {
        this.trackedJobRun = Object.assign({}, this.trackedJobRun, { status: 'failed' })
        this.error = this.errMsg(e)
      } finally {
        this.jobRunBusy = false
      }
    },
    async cleanupJobs (mode) {
      if (mode !== 'all' && mode !== 'stale') return
      if (mode === 'all' && !window.confirm('Stop every running job? This affects all admin job workflows.')) return
      this.jobCleanupBusy = mode
      try {
        await axios.post(
          `${apiDomain}jobs/cleanup?mode=${encodeURIComponent(mode)}`,
          {},
          { headers: this.authHeaders(), timeout: 30000 }
        )
        if (mode === 'all' && this.trackedJobRun) {
          this.trackedJobRun = Object.assign({}, this.trackedJobRun, { status: 'stopped' })
        }
        this.notice = mode === 'all' ? 'All running jobs were stopped.' : 'Stale job cleanup completed.'
        this.stopJobPolling()
        await this.fetchJobScope()
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.jobCleanupBusy = ''
      }
    },
    scheduleJobPoll () {
      if (this.jobPollTimer || !this.isTrackedJobActive) return
      this.jobPollTimer = setTimeout(this.pollTrackedJob, 5000)
    },
    stopJobPolling () {
      if (!this.jobPollTimer) return
      clearTimeout(this.jobPollTimer)
      this.jobPollTimer = null
    },
    async pollTrackedJob () {
      this.jobPollTimer = null
      if (!this.isTrackedJobActive) return
      try {
        let run = null
        if (this.jobRunId) {
          const { data } = await axios.get(
            `${apiDomain}jobs/runs/${encodeURIComponent(this.jobRunId)}`,
            { headers: this.authHeaders() }
          )
          run = this.unwrap(data, ['run', 'data']) || data
        } else {
          const { data } = await axios.get(`${apiDomain}jobs/runs?limit=25`, { headers: this.authHeaders() })
          const runs = this.jobRunsFromPayload(data)
          run = runs.find((item) => this.pick(item, ['jobKey', 'job_key', 'job'], '') === CONTENT_JOB_KEY)
        }
        if (run) this.setTrackedJobRun(run)
        if (!this.isTrackedJobActive) {
          await Promise.all([this.fetchContent(), this.fetchJobScope()])
          return
        }
      } catch (e) {
        // WebSocket is primary; polling keeps the last good snapshot on transient failures.
      }
    },
    queryString (pageOverride, overrides) {
      const options = overrides || {}
      const p = new URLSearchParams()
      p.set('type', this.filters.type || 'all')
      p.set('status', options.status || this.currentStatus || 'all')
      p.set('q', this.filters.q || '')
      p.set('page', String(pageOverride || this.page || 1))
      p.set('perPage', String(options.perPage || this.perPage || 50))
      return p.toString()
    },
    async fetchContent () {
      this.loading = true
      try {
        if (this.currentStatus === 'completed') {
          await this.fetchCompletedContent()
          this.requestContentSnapshot()
          this.error = null
          return
        }
        const { data } = await axios.get(
          `${apiDomain}admin/content?${this.queryString()}`,
          { headers: this.authHeaders() }
        )
        const payload = this.unwrap(data, ['data', 'content']) || data || {}
        const rows = this.asList(payload, ['rows', 'items', 'results'])
        this.completedRowsCache = []
        this.rows = rows
        this.counts = Object.assign(
          {},
          this.normalizeCounts(data || {}),
          this.normalizeCounts(payload)
        )
        this.pagination = this.normalizePagination(payload, rows)
        this.page = this.pagination.page || this.page
        this.perPage = this.pagination.perPage || this.perPage
        this.rememberRows(rows)
        this.ensureGroupState(rows)
        this.requestContentSnapshot()
        this.error = null
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.loading = false
      }
    },
    async fetchCompletedContent () {
      const batchSize = 250
      const fetchPage = async (page) => {
        const { data } = await axios.get(
          `${apiDomain}admin/content?${this.queryString(page, { status: 'all', perPage: batchSize })}`,
          { headers: this.authHeaders() }
        )
        const payload = this.unwrap(data, ['data', 'content']) || data || {}
        return {
          data,
          payload,
          rows: this.asList(payload, ['rows', 'items', 'results']),
          pagination: this.normalizePagination(payload, this.asList(payload, ['rows', 'items', 'results']))
        }
      }

      const first = await fetchPage(1)
      const pages = [first]
      if (first.pagination.totalPages > 1) {
        const requests = []
        for (let page = 2; page <= first.pagination.totalPages; page += 1) requests.push(fetchPage(page))
        pages.push(...await Promise.all(requests))
      }

      const completedRows = pages
        .reduce((rows, result) => rows.concat(result.rows), [])
        .filter((row) => this.isCompletedStatus(this.statusOf(row)))
      const total = completedRows.length
      const totalPages = Math.max(1, Math.ceil(total / Math.max(1, this.perPage)))
      const page = Math.max(1, Math.min(totalPages, Number(this.page) || 1))
      const start = (page - 1) * this.perPage

      this.completedRowsCache = completedRows
      this.rows = completedRows.slice(start, start + this.perPage)
      this.counts = Object.assign(
        {},
        this.normalizeCounts(first.data || {}),
        this.normalizeCounts(first.payload)
      )
      this.pagination = { total, filtered: total, page, perPage: this.perPage, totalPages }
      this.page = page
      this.rememberRows(completedRows)
      this.ensureGroupState(completedRows)
    },
    debouncedFilterChange () {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null
        this.onFilterChange()
      }, 300)
    },
    onFilterChange () {
      this.clearSelection()
      this.page = 1
      this.fetchContent()
    },
    selectStatus (status) {
      this.filters.status = status
      this.onFilterChange()
    },
    setTypeFilter (value) {
      if (this.filters.type === value) return
      this.filters.type = value
      this.onFilterChange()
    },
    setStatusFilter (value) {
      if (this.filters.status === value) return
      this.filters.status = value
      this.onFilterChange()
    },
    resetFilters () {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
      this.filters.q = ''
      this.filters.type = 'all'
      this.filters.status = 'all'
      this.onFilterChange()
    },
    selectedOptionLabel (options, value) {
      const option = (options || []).find((entry) => entry.value === value)
      return option ? option.text : ''
    },
    setPage (page) {
      const next = Math.max(1, Math.min(this.totalPages, Number(page) || 1))
      if (next === this.page) return
      this.page = next
      this.fetchContent()
    },
    rememberRows (rows) {
      rows.forEach((row) => {
        const id = this.rowIdentity(row)
        if (id) this.$set(this.loadedRowsById, id, row)
      })
    },
    ensureGroupState (rows) {
      rows.forEach((row) => {
        const type = this.rowType(row) || 'other'
        if (this.groupOpen[type] === undefined) this.$set(this.groupOpen, type, true)
      })
    },
    async selectAllFiltered () {
      if (!this.rows.length) return

      if (!this.hasPagination || this.totalPages <= 1) {
        this.selectRows(this.rows)
        return
      }

      this.selectingFiltered = true
      try {
        const allRows = this.rows.slice()
        const seen = {}
        allRows.forEach((row) => {
          const id = this.rowIdentity(row)
          if (id) seen[id] = true
        })

        for (let page = 1; page <= this.totalPages; page += 1) {
          if (page === this.page) continue
          const pageRows = await this.fetchContentPage(page)
          pageRows.forEach((row) => {
            const id = this.rowIdentity(row)
            if (!id || seen[id]) return
            seen[id] = true
            allRows.push(row)
          })
        }

        this.rememberRows(allRows)
        this.selectRows(allRows)
        this.notice = `${allRows.length} filtered ${allRows.length === 1 ? 'part' : 'parts'} selected.`
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.selectingFiltered = false
      }
    },
    async fetchContentPage (page) {
      if (this.currentStatus === 'completed' && this.completedRowsCache.length) {
        const start = (page - 1) * this.perPage
        return this.completedRowsCache.slice(start, start + this.perPage)
      }
      const { data } = await axios.get(
        `${apiDomain}admin/content?${this.queryString(page)}`,
        { headers: this.authHeaders() }
      )
      const payload = this.unwrap(data, ['data', 'content']) || data || {}
      return this.asList(payload, ['rows', 'items', 'results'])
    },
    selectRows (rows) {
      rows.forEach((row) => {
        const id = this.rowIdentity(row)
        if (id) this.$set(this.selectedIds, id, true)
      })
    },
    toggleVisibleSelection (checked) {
      this.rows.forEach((row) => {
        this.toggleRowSelection(row, checked)
      })
    },
    toggleGroupSelection (group, checked) {
      group.rows.forEach((row) => {
        this.toggleRowSelection(row, checked)
      })
    },
    toggleRowSelection (row, checked) {
      const id = this.rowIdentity(row)
      if (!id) return
      if (checked) this.$set(this.selectedIds, id, true)
      else this.$delete(this.selectedIds, id)
    },
    clearSelection () {
      this.selectedIds = {}
    },
    isSelected (row) {
      const id = this.rowIdentity(row)
      return !!(id && this.selectedIds[id])
    },
    groupAllSelected (group) {
      return group.rows.length > 0 && group.rows.every((row) => this.isSelected(row))
    },
    groupSomeSelected (group) {
      return group.rows.some((row) => this.isSelected(row)) && !this.groupAllSelected(group)
    },
    toggleGroup (type) {
      this.$set(this.groupOpen, type, !this.isGroupOpen(type))
    },
    isGroupOpen (type) {
      return this.groupOpen[type] !== false
    },
    async researchSelected () {
      const ids = this.selectedIdsArray
      if (!ids.length) return
      if (ids.length > 10 && !window.confirm(`Generate content for ${ids.length} selected parts now? Each item can take 15–40 seconds and items run one at a time.`)) {
        return
      }

      this.generating = true
      let completed = 0
      let failed = 0
      try {
        const rows = this.selectedRows.slice()
        for (const row of rows) {
          this.notice = `Generating ${completed + failed + 1} of ${rows.length}: ${this.partName(row)}`
          const generated = await this.generateRow(row, false, false)
          if (generated) completed += 1
          else failed += 1
        }
        this.notice = `${completed} ${completed === 1 ? 'part' : 'parts'} generated${failed ? `; ${failed} failed.` : '.'}`
        if (!failed) this.error = null
        await this.fetchContent()
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        this.generating = false
      }
    },
    async approveSelected () {
      if (!this.canApproveSelected) return
      const rows = this.selectedRows.slice()
      this.approving = true
      try {
        for (const row of rows) {
          await this.approveRow(row, false)
        }
        this.notice = `${rows.length} ${rows.length === 1 ? 'part' : 'parts'} approved.`
        await this.fetchContent()
      } catch (e) {
        this.error = this.errMsg(e)
      } finally {
        rows.forEach((row) => {
          const id = this.rowIdentity(row)
          if (id) this.$set(this.busy, id, false)
        })
        this.approving = false
      }
    },
    async approveRow (row, showNotice) {
      const id = this.rowIdentity(row)
      if (!id) return
      this.$set(this.busy, id, 'approve')
      try {
        const { data } = await axios.post(
          `${this.contentEndpoint(row)}/approve`,
          {},
          { headers: this.authHeaders() }
        )
        this.applyRowResponse(id, data, { contentStatus: 'approved', status: 'approved' })
        if (showNotice !== false) {
          this.notice = `${this.partName(row)} approved.`
          this.fetchContent()
        }
      } finally {
        this.$set(this.busy, id, false)
      }
    },
    async regenerateRow (row, throwOnError) {
      return this.generateRow(row, throwOnError, true)
    },
    async generateRow (row, throwOnError, showNotice) {
      const id = this.rowIdentity(row)
      if (!id) return false
      this.$set(this.busy, id, 'regenerate')
      try {
        const { data } = await axios.post(
          `${this.contentEndpoint(row)}/generate`,
          {},
          { headers: this.authHeaders(), timeout: GENERATE_TIMEOUT_MS }
        )
        const generatedRow = this.unwrap(data, ['row', 'part', 'data']) || data || {}
        this.applyRowResponse(id, generatedRow, { contentStatus: 'generated', status: 'generated' })
        if (this.reviewSourceRow && this.rowIdentity(this.reviewSourceRow) === id) {
          this.reviewItem = generatedRow
        }
        if (showNotice !== false) {
          this.notice = `${this.partName(row)} regenerated.`
          this.fetchContent()
        }
        this.error = null
        return true
      } catch (e) {
        const message = this.errMsg(e)
        await this.refreshContentRow(row)
        this.error = message
        if (throwOnError) throw e
        return false
      } finally {
        this.$set(this.busy, id, false)
      }
    },
    async refreshContentRow (row) {
      const id = this.rowIdentity(row)
      if (!id) return
      try {
        const { data } = await axios.get(this.contentEndpoint(row), { headers: this.authHeaders() })
        this.applyRowResponse(id, data)
      } catch (e) {
        // Preserve the generation error when the best-effort row refresh also fails.
      }
    },
    applyRowResponse (id, data, fallback) {
      const row = this.unwrap(data, ['row', 'part', 'data']) || data || {}
      const base = this.loadedRowsById[id] || this.rows.find((entry) => String(this.rowIdentity(entry)) === String(id)) || {}
      const next = Object.assign({}, base, fallback || {}, row)
      this.replaceRow(id, next)
    },
    replaceRow (id, next) {
      if (!id) return
      this.$set(this.loadedRowsById, id, next)
      const index = this.rows.findIndex((row) => String(this.rowIdentity(row)) === String(id))
      if (index >= 0) this.$set(this.rows, index, next)
      if (this.reviewSourceRow && String(this.rowIdentity(this.reviewSourceRow)) === String(id)) {
        this.reviewSourceRow = next
      }
    },
    async openReview (row) {
      const id = this.rowIdentity(row)
      if (!id) return
      this.reviewSourceRow = row
      this.reviewItem = null
      this.reviewError = ''
      this.reviewBusy = ''
      this.reviewLoading = true
      this.reviewModalOpen = true

      try {
        const { data } = await axios.get(
          this.contentEndpoint(row),
          { headers: this.authHeaders() }
        )
        this.reviewItem = this.unwrap(data, ['data']) || data
      } catch (e) {
        this.reviewError = this.errMsg(e)
      } finally {
        this.reviewLoading = false
      }
    },
    async approveReview () {
      if (!this.reviewSourceRow) return
      this.reviewBusy = 'approve'
      try {
        await this.approveRow(this.reviewSourceRow, true)
        this.reviewModalOpen = false
      } catch (e) {
        this.reviewError = this.errMsg(e)
      } finally {
        this.reviewBusy = ''
      }
    },
    async regenerateReview () {
      if (!this.reviewSourceRow) return
      this.reviewBusy = 'regenerate'
      try {
        await this.regenerateRow(this.reviewSourceRow, true)
      } catch (e) {
        this.reviewError = this.errMsg(e)
      } finally {
        this.reviewBusy = ''
      }
    },
    clearReview () {
      this.reviewLoading = false
      this.reviewError = ''
      this.reviewItem = null
      this.reviewSourceRow = null
      this.reviewBusy = ''
    },
    toggleRowDetails (row) {
      const id = this.rowIdentity(row)
      if (!id) return
      this.$set(this.rowDetailsOpen, id, !this.rowDetailsOpen[id])
    },
    isRowExpanded (row) {
      const id = this.rowIdentity(row)
      return !!(id && this.rowDetailsOpen[id])
    },
    markImageFailed (row) {
      this.$set(row, '_imageFailed', true)
    },
    fieldPresent (row, key) {
      const presence = row && row.presence ? row.presence : {}
      const presenceValue = this.pick(presence, this.fieldAliases(key), undefined)
      if (presenceValue !== undefined) return !!presenceValue

      const content = row && row.content ? row.content : {}
      const value = this.pick(content, this.fieldAliases(key), undefined)
      if (value === false || value === null || value === undefined || value === '') return false
      if (Array.isArray(value)) return value.length > 0
      return true
    },
    fieldInlineValue (row, key) {
      if (['tier', 'releasedYear', 'releasedDate', 'discontinued', 'discontinuedYear'].indexOf(key) === -1) return ''
      const content = row && row.content ? row.content : {}
      const value = this.pick(content, this.fieldAliases(key), '')
      if (value === true || value === false || value === undefined || value === null || value === '') return ''
      if (typeof value === 'object') return ''
      return String(value)
    },
    fieldTooltip (row, field) {
      const state = this.fieldPresent(row, field.key) ? 'present' : 'missing'
      const extra = this.fieldInlineValue(row, field.key)
      const verified = this.isUnverifiedField(row, field.key) ? ' - unverified' : ''
      return `${field.label}: ${state}${extra ? ' (' + extra + ')' : ''}${verified}`
    },
    fieldDetailText (row, field) {
      if (!this.fieldPresent(row, field.key)) return 'Missing'
      return this.fieldInlineValue(row, field.key) || 'Present'
    },
    fieldSummary (row) {
      return `${this.completenessText(row)} content fields present`
    },
    completenessCount (row) {
      return CONTENT_FIELDS.reduce((count, field) => {
        return count + (this.fieldPresent(row, field.key) ? 1 : 0)
      }, 0)
    },
    completenessPercent (row) {
      return Math.round((this.completenessCount(row) / CONTENT_FIELDS.length) * 100)
    },
    completenessText (row) {
      return `${this.completenessCount(row)} / ${CONTENT_FIELDS.length}`
    },
    isContentComplete (row) {
      return this.completenessCount(row) === CONTENT_FIELDS.length
    },
    statusOf (row) {
      if (!row) return 'empty'
      const status = this.pick(row, ['contentStatus', 'content_status', 'status'], '')
      if (status) return String(status).toLowerCase()
      return this.isContentComplete(row) ? 'generated' : 'empty'
    },
    statusLabel (row) {
      if (this.isCompletedStatus(this.statusOf(row))) return 'Completed'
      return this.prettyLabel(this.statusOf(row))
    },
    statusClass (row) {
      const status = this.statusOf(row)
      return 'st-' + (this.isCompletedStatus(status) ? 'completed' : status.replace(/[^a-z0-9_-]/g, '-'))
    },
    isCompletedStatus (status) {
      return status === 'generated' || status === 'approved'
    },
    isPending (row) {
      return this.isPendingStatus(this.statusOf(row))
    },
    isPendingStatus (status) {
      return status === 'pending' || status === 'running' || status === 'queued'
    },
    canApproveRow (row) {
      const status = this.statusOf(row)
      return status === 'generated' || status === 'needs_review'
    },
    isRowBusy (row) {
      const id = this.rowIdentity(row)
      return !!(id && this.busy[id])
    },
    rowBusyLabel (row, fallback) {
      const id = this.rowIdentity(row)
      return id && this.busy[id] ? 'Working...' : fallback
    },
    isUnverifiedField (row, key) {
      const target = this.normalizeFieldName(key)
      return this.unverifiedList(row).some((field) => this.normalizeFieldName(field) === target)
    },
    isReviewFieldUnverified (key) {
      const target = this.normalizeFieldName(key)
      return this.reviewUnverified.some((field) => this.normalizeFieldName(field) === target)
    },
    unverifiedList (row) {
      return this.normalizeArray(row && row.unverified)
    },
    reviewFieldValue (key) {
      const content = this.reviewContent || {}
      return this.pick(content, this.fieldAliases(key), '')
    },
    formattedReviewValue (key) {
      const value = this.reviewFieldValue(key)
      if (value === undefined || value === null || value === '') return 'No value'
      if (typeof value === 'boolean') return value ? 'Yes' : 'No'
      if (Array.isArray(value)) return value.join(', ')
      if (typeof value === 'object') return JSON.stringify(value, null, 2)
      return String(value)
    },
    confidenceLabel (value) {
      if (value === undefined || value === null || value === '') return 'N/A'
      const num = Number(value)
      if (isNaN(num)) return String(value)
      if (num <= 1) return Math.round(num * 100) + '%'
      return Math.round(num) + '%'
    },
    sourceKey (source, index) {
      return this.sourceUrl(source) || this.sourceTitle(source, index) || index
    },
    sourceUrl (source) {
      if (!source) return ''
      if (typeof source === 'string') return source.indexOf('http') === 0 ? source : ''
      return this.pick(source, ['url', 'href', 'sourceUrl', 'link'], '')
    },
    sourceTitle (source, index) {
      if (!source) return `Source ${index + 1}`
      if (typeof source === 'string') return source
      return this.pick(source, ['title', 'label', 'name', 'domain'], '') || this.sourceUrl(source) || `Source ${index + 1}`
    },
    rowIdentity (row) {
      const type = this.rowType(row)
      const id = this.partId(row)
      if (PART_TYPES.indexOf(type) <= 0 || !id) return ''
      return `${type}:${id}`
    },
    contentEndpoint (row) {
      const type = this.rowType(row)
      const partId = this.partId(row)
      if (PART_TYPES.indexOf(type) <= 0 || !partId) return ''
      return `${apiDomain}admin/content/${encodeURIComponent(type)}/${encodeURIComponent(partId)}`
    },
    partId (row) {
      const value = this.pick(row, ['partId', 'part_id', 'catalogPartId', 'catalog_part_id'], '')
      return value === undefined || value === null ? '' : String(value)
    },
    contentRowId (row) {
      const value = this.pick(row, ['id', 'contentId', 'content_id'], '')
      return value === undefined || value === null ? '' : String(value)
    },
    contentId (row) {
      return this.partId(row)
    },
    rowKey (row) {
      return this.rowIdentity(row) || `${this.rowType(row)}-${this.partName(row)}`
    },
    rowType (row) {
      const type = this.pick(row, ['type', 'partType', 'category'], '')
      return this.catalogPartType(type)
    },
    partName (row) {
      if (!row) return 'Selected part'
      return this.pick(row, ['partModel', 'model', 'name', 'title'], '') || `Part #${this.partId(row) || 'unknown'}`
    },
    partUrl (row) {
      const id = this.partId(row)
      const type = this.rowType(row)
      if (!id || !type) return '/builder/home'
      return `/builder/${encodeURIComponent(type)}?view=slider&id=${encodeURIComponent(id)}&redir=true`
    },
    partInitials (row) {
      return this.prettyType(this.rowType(row)).slice(0, 3).toUpperCase()
    },
    availabilityLabel (row) {
      if (!row || row.isAvailable === undefined || row.isAvailable === null) return 'Availability unknown'
      return row.isAvailable ? 'Available' : 'Unavailable'
    },
    rowMeta (row) {
      if (!row) return ''
      const bits = []
      if (row.contentModel) bits.push(row.contentModel)
      if (row.promptVersion) bits.push(row.promptVersion)
      if (row.sourcesCount) bits.push(`${row.sourcesCount} sources`)
      if (row.retries) bits.push(`${row.retries} retries`)
      if (row.generatedAt) bits.push(`Generated ${this.shortDate(row.generatedAt)}`)
      if (row.reviewedAt) bits.push(`Reviewed ${this.shortDate(row.reviewedAt)}`)
      return bits.join(' - ')
    },
    rowImage (row) {
      if (!row) return ''
      return this.imageValue(this.pick(row, ['image', 'img', 'imgUrl', 'imageUrl', 'mainImg', 'thumbnail'], ''))
    },
    imageValue (value) {
      if (!value) return ''
      if (Array.isArray(value)) return this.imageValue(value[0])
      if (typeof value === 'object') return this.pick(value, ['url', 'src', 'main', 'thumbnail', 'imageUrl'], '')
      if (typeof value === 'string') {
        const trimmed = value.trim()
        if (!trimmed || trimmed === 'N/A' || trimmed === 'undefined' || trimmed === 'null') return ''
        if (trimmed.charAt(0) === '[') {
          try {
            const parsed = JSON.parse(trimmed)
            return this.imageValue(parsed)
          } catch (e) {
            return trimmed
          }
        }
        return trimmed
      }
      return ''
    },
    catalogPartType (type) {
      const value = String(type || '').toLowerCase()
      if (value === 'processor') return 'cpu'
      if (value === 'graphics' || value === 'video-card' || value === 'video_card') return 'gpu'
      if (value === 'memory') return 'ram'
      if (value === 'power-supply' || value === 'power_supply') return 'psu'
      return value
    },
    prettyType (type) {
      const map = {
        cpu: 'CPU',
        gpu: 'GPU',
        psu: 'PSU',
        ram: 'RAM'
      }
      const value = String(type || '')
      return map[value] || this.prettyLabel(value)
    },
    prettyLabel (value) {
      return String(value || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase())
    },
    statusAliases (status) {
      const value = String(status || '')
      const aliases = [value, this.camelCase(value)]
      if (value === 'empty') aliases.push('missing', 'missingContent')
      if (value === 'needs_review') aliases.push('needsReview')
      return aliases
    },
    fieldAliases (key) {
      const value = String(key || '')
      const snake = value
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .toLowerCase()
      const aliases = [value, snake, this.camelCase(snake)]
      const map = {
        originalDescription: ['original_description'],
        seoSummary: ['seo_summary'],
        bestFor: ['best_for'],
        uniqueNote: ['unique_note'],
        releasedYear: ['released_year', 'released'],
        releasedDate: ['released_date'],
        discontinuedYear: ['discontinued_year'],
        officialUrl: ['official_url']
      }
      return aliases.concat(map[value] || [])
    },
    camelCase (value) {
      return String(value || '').replace(/_([a-z])/g, (m, chr) => chr.toUpperCase())
    },
    normalizeFieldName (value) {
      return String(value || '')
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
    },
    normalizeArray (value) {
      if (!value) return []
      if (Array.isArray(value)) return value.filter((entry) => entry !== undefined && entry !== null && entry !== '')
      if (typeof value === 'string') {
        return value.split(/[,|]/).map((entry) => entry.trim()).filter((entry) => !!entry)
      }
      return [value]
    },
    normalizeCounts (payload) {
      const raw = this.pick(payload, ['counts', 'summary', 'totals'], {}) || {}
      const counts = Object.assign({}, raw)
      ;['total', 'filtered'].forEach((key) => {
        if (payload && payload[key] !== undefined && payload[key] !== null) counts[key] = payload[key]
      })
      return counts
    },
    normalizePagination (payload, rows) {
      const pagination = this.pick(payload, ['pagination', 'meta'], {}) || {}
      const total = this.firstNumber([
        this.pick(pagination, ['total', 'totalRows', 'count'], null),
        this.pick(payload, ['total', 'totalRows'], null)
      ], rows.length)
      const filtered = this.firstNumber([
        this.pick(pagination, ['filtered', 'filteredRows'], null),
        this.pick(payload, ['filtered'], null)
      ], total)
      const page = this.firstNumber([
        this.pick(pagination, ['page', 'currentPage'], null),
        this.pick(payload, ['page'], null)
      ], this.page)
      const perPage = this.firstNumber([
        this.pick(pagination, ['perPage', 'limit'], null),
        this.pick(payload, ['perPage', 'limit'], null)
      ], this.perPage)
      const totalPages = this.firstNumber([
        this.pick(pagination, ['totalPages', 'pages', 'lastPage'], null),
        this.pick(payload, ['totalPages', 'pages', 'lastPage'], null)
      ], Math.max(1, Math.ceil(filtered / Math.max(1, perPage))))
      return { total, filtered, page, perPage, totalPages }
    },
    totalCount () {
      const direct = this.numOrNull(this.pick(this.counts, ['total', 'totalParts', 'parts', 'count'], null))
      if (direct !== null) return direct
      const byStatus = this.statusMap()
      const sum = Object.keys(byStatus)
        .filter((key) => key !== 'all' && key !== 'total')
        .reduce((total, key) => total + (Number(byStatus[key]) || 0), 0)
      return sum || this.rows.length
    },
    statusCount (status) {
      const byStatus = this.statusMap()
      const value = this.numOrNull(this.pick(byStatus, this.statusAliases(status), null))
      if (value !== null) return value
      return this.rows.filter((row) => this.statusOf(row) === status).length
    },
    statusMap () {
      return this.pick(this.counts, ['status', 'statuses', 'byStatus'], this.counts) || {}
    },
    asList (obj, keys) {
      if (Array.isArray(obj)) return obj
      const found = this.unwrap(obj, keys || [])
      return Array.isArray(found) ? found : []
    },
    unwrap (obj, keys) {
      if (!obj) return null
      for (const key of keys) {
        if (obj[key] !== undefined && obj[key] !== null) return obj[key]
      }
      return null
    },
    pick (obj, keys, fallback) {
      if (!obj) return fallback
      for (const key of keys) {
        if (obj[key] !== undefined && obj[key] !== null) return obj[key]
      }
      return fallback
    },
    numOrNull (value) {
      if (value === undefined || value === null || value === '') return null
      const n = Number(value)
      return isNaN(n) ? null : n
    },
    firstNumber (values, fallback) {
      for (const value of values) {
        const number = this.numOrNull(value)
        if (number !== null) return number
      }
      return fallback
    },
    shortDate (value) {
      if (!value) return ''
      const d = new Date(value)
      if (isNaN(d.getTime())) return String(value)
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    },
    errMsg (e) {
      const data = e && e.response && e.response.data
      return (data && data.error && data.error.message) ||
        (data && data.message) ||
        (e && e.code === 'ECONNABORTED' ? 'The request timed out before generation completed.' : '') ||
        (e && e.message) ||
        'Request failed'
    }
  }
}
</script>

<style>
.gc-admin-dropdown {
  position: relative;
  z-index: 70;
  min-width: 180px;
}

.gc-admin-dropdown.show {
  z-index: 5000;
}

.gc-admin-dropdown .gc-admin-dropdown-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  height: 31px;
  padding: 0 9px;
  border: 1px solid rgba(67, 72, 87, 0.95) !important;
  border-radius: 6px;
  background: rgba(10, 13, 20, 0.56) !important;
  color: #cfd2da !important;
  font-size: 12px;
  line-height: 31px;
  text-align: left;
  text-decoration: none !important;
  outline: none;
  box-shadow: none !important;
}

.gc-admin-dropdown.show .gc-admin-dropdown-toggle,
.gc-admin-dropdown .gc-admin-dropdown-toggle:focus,
.gc-admin-dropdown .gc-admin-dropdown-toggle:hover {
  border-color: rgba(25, 151, 198, 0.85) !important;
  background: rgba(10, 13, 20, 0.78) !important;
  color: #eaf7fc !important;
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.12) !important;
}

.gc-admin-dropdown-caret {
  flex: 0 0 auto;
  color: #7f899b;
  font-size: 11px;
  line-height: 1;
}

.gc-admin-dropdown.show .gc-admin-dropdown-caret {
  color: #1fb2e8;
}

.gc-admin-dropdown-menu {
  z-index: 5000;
  min-width: 100%;
  max-height: 280px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid rgba(25, 151, 198, 0.48);
  border-radius: 7px;
  background:
    radial-gradient(circle at 20% 0%, rgba(25, 151, 198, 0.14), transparent 42%),
    #080c12;
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.42);
}

.gc-admin-dropdown-menu .dropdown-item {
  min-height: 28px;
  padding: 6px 9px;
  border-radius: 5px;
  background: transparent;
  color: #cfd2da;
  font-size: 12px;
  line-height: 1.2;
}

.gc-admin-dropdown-menu .dropdown-item:hover,
.gc-admin-dropdown-menu .dropdown-item:focus {
  background: rgba(25, 151, 198, 0.12);
  color: #eaf7fc;
}

.gc-admin-dropdown-menu .dropdown-item.active,
.gc-admin-dropdown-menu .active > .dropdown-item,
.gc-admin-dropdown-menu .dropdown-item:active {
  background: rgba(25, 151, 198, 0.22);
  color: #7bccee;
  font-weight: 800;
}
</style>

<style scoped>
.health-page {
  padding: 4px 8px 78px;
  color: #cfd2da;
}

.label {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  border-bottom: 1px solid rgba(67, 72, 87, 0.9);
}

.label h4 {
  margin: 0 0 6px;
  color: #d8dde8;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #1997c6;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.intro {
  max-width: 760px;
  margin: 0.6rem 0 1.1rem;
  color: #a4acbc;
  font-size: 14px;
  line-height: 1.55;
}

.alert,
.notice {
  margin: 12px 0;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
}

.alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border: 1px solid rgba(232, 79, 106, 0.5);
  background: rgba(90, 34, 48, 0.48);
  color: #f3c6cf;
}

.alert-retry {
  border: 0;
  background: transparent;
  color: #f3c6cf;
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

.notice {
  border: 1px solid rgba(25, 151, 198, 0.5);
  background: rgba(20, 54, 70, 0.5);
  color: #aee0f3;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  margin: 18px 0 8px;
}

.kpi-card {
  display: grid;
  gap: 6px;
  padding: 16px;
  border: 1px solid rgba(67, 72, 87, 0.92);
  border-radius: 8px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.08), transparent 38%),
    rgba(14, 18, 27, 0.78);
}

.kpi-label {
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.kpi-value {
  color: #eaf0f7;
  font-size: 30px;
  font-weight: 800;
  line-height: 1;
}

.kpi-card.tone-bad {
  border-color: rgba(232, 79, 106, 0.4);
}

.kpi-card.tone-bad .kpi-value {
  color: #f0a1af;
}

.kpi-card.tone-warn {
  border-color: rgba(224, 192, 126, 0.36);
}

.kpi-card.tone-warn .kpi-value {
  color: #e0c07e;
}

.kpi-card.tone-good {
  border-color: rgba(27, 201, 142, 0.42);
}

.kpi-card.tone-good .kpi-value {
  color: #89dfbd;
}

.kpi-card.tone-muted .kpi-value {
  color: #aeb5c5;
}

.coverage-progress {
  display: grid;
  gap: 8px;
  margin: 10px 0 18px;
  padding: 0 2px;
}

.coverage-progress-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;
}

.coverage-progress-head > div {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 7px;
}

.coverage-progress-title {
  color: #d7deea;
  font-size: 12px;
  font-weight: 800;
}

.coverage-progress-detail {
  color: #7f899b;
  font-size: 11px;
}

.coverage-progress-head > strong {
  color: #89dfbd;
  font-size: 14px;
  line-height: 1;
}

.coverage-progress-track {
  display: flex;
  width: 100%;
  height: 7px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(73, 82, 101, 0.44);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.42);
}

.coverage-progress-segment {
  display: block;
  flex: 0 0 auto;
  height: 100%;
  min-width: 2px;
  transition: width 0.25s ease;
}

.segment-approved {
  background: #1bc98e;
}

.segment-progress {
  background: #e0c07e;
}

.segment-failed {
  background: #e84f6a;
}

.coverage-progress-legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
  color: #8f98aa;
  font-size: 10.5px;
}

.coverage-progress-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.coverage-progress-legend i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #4d5668;
}

.coverage-progress-legend .legend-approved i { background: #1bc98e; }
.coverage-progress-legend .legend-progress i { background: #e0c07e; }
.coverage-progress-legend .legend-failed i { background: #e84f6a; }

.content-job-panel {
  margin-top: 18px;
  padding: 14px;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 9px;
  background:
    radial-gradient(circle at 12% 0%, rgba(25, 151, 198, 0.1), transparent 36%),
    rgba(8, 12, 18, 0.5);
}

.content-job-head,
.content-job-head > div,
.job-progress-copy,
.scope-summary {
  display: flex;
  align-items: center;
}

.content-job-head {
  justify-content: space-between;
  gap: 16px;
}

.content-job-head > div {
  align-items: flex-start;
  flex-direction: column;
  gap: 3px;
}

.content-job-head h5,
.content-job-head p {
  margin: 0;
}

.content-job-head h5 {
  color: #eaf0f7;
  font-size: 15px;
  font-weight: 800;
}

.content-job-head p {
  color: #8993a5;
  font-size: 11.5px;
}

.live-state {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #8f98aa;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.live-state > span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e0c07e;
  box-shadow: 0 0 0 3px rgba(224, 192, 126, 0.1);
}

.live-state.connected {
  color: #89dfbd;
}

.live-state.connected > span {
  background: #1bc98e;
  box-shadow: 0 0 0 3px rgba(27, 201, 142, 0.12);
}

.job-controls {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 13px;
}

.job-controls label {
  display: grid;
  gap: 5px;
  margin: 0;
  color: #8f98aa;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.job-control-input {
  min-width: 150px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 7px;
  background: rgba(10, 13, 20, 0.72);
  color: #d7dce8;
  font-size: 12px;
  outline: none;
}

.job-control-input:focus {
  border-color: rgba(25, 151, 198, 0.85);
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.12);
}

.job-limit {
  min-width: 86px;
  width: 86px;
}

.scope-summary {
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  color: #8f98aa;
  font-size: 11.5px;
}

.scope-summary strong {
  color: #7bccee;
}

.scope-chip {
  padding: 3px 7px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 999px;
  background: rgba(20, 25, 35, 0.68);
}

.scope-items {
  margin-top: 9px;
  color: #8f98aa;
  font-size: 11px;
}

.scope-items summary {
  cursor: pointer;
  font-weight: 800;
}

.scope-items ul {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 6px;
  max-height: 190px;
  margin: 8px 0 0;
  padding: 0;
  overflow: auto;
  list-style: none;
}

.scope-items li {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 7px;
  padding: 7px 8px;
  border: 1px solid rgba(67, 72, 87, 0.62);
  border-radius: 6px;
  background: rgba(10, 13, 20, 0.48);
}

.scope-items li > span {
  color: #7bccee;
  font-size: 9.5px;
  font-weight: 800;
  text-transform: uppercase;
}

.scope-items li > strong {
  overflow: hidden;
  color: #cfd2da;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scope-items li > small {
  color: #687386;
}

.job-inline-error {
  margin-top: 11px;
  color: #f0a1af;
  font-size: 11.5px;
  font-weight: 700;
}

.content-job-progress {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(67, 72, 87, 0.66);
}

.job-progress-copy {
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 7px;
  color: #8993a5;
  font-size: 11px;
}

.job-progress-copy strong {
  margin-right: auto;
  color: #d7dce8;
}

.content-job-progress .progress-track {
  width: 100%;
  height: 7px;
}

.job-log-panel {
  margin-top: 9px;
  color: #8f98aa;
  font-size: 11px;
}

.job-log-panel summary {
  cursor: pointer;
  font-weight: 800;
}

.job-log-panel ol {
  max-height: 180px;
  margin: 8px 0 0;
  padding: 9px 9px 9px 28px;
  overflow: auto;
  border-radius: 6px;
  background: rgba(5, 8, 13, 0.7);
  color: #aeb5c5;
  font-family: Consolas, monospace;
}

.tabs-row {
  position: relative;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin: 28px 0 10px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.tab {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 9px 14px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #9aa3b4;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
}

.tab:hover {
  color: #d7dce8;
}

.tab.active {
  color: #1fb2e8;
  border-bottom-color: #1fb2e8;
}

.tab-count {
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(58, 63, 77, 0.6);
  color: #aeb5c5;
  font-size: 10.5px;
  font-weight: 800;
}

.filter-panel {
  position: relative;
  z-index: 60;
  margin-bottom: 10px;
  padding: 11px 12px 12px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 8px;
  background: rgba(8, 12, 18, 0.42);
}

.filter-panel-head,
.filter-panel-head > div {
  display: flex;
  align-items: center;
  gap: 9px;
}

.filter-panel-head {
  justify-content: space-between;
}

.filter-title {
  color: #d7dce8;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.filter-summary {
  color: #7f899b;
  font-size: 11.5px;
}

.clear-filters {
  padding: 0;
  border: 0;
  background: transparent;
  color: #7bccee;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 800;
}

.clear-filters:hover:not(:disabled) {
  color: #eaf7fc;
  text-decoration: underline;
}

.clear-filters:disabled {
  opacity: 0.5;
  cursor: default;
}

.filters {
  position: relative;
  z-index: 65;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0 0;
}

.hfilter {
  height: 31px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(10, 13, 20, 0.56);
  color: #cfd2da;
  padding: 0 9px;
  font-size: 12px;
  outline: none;
}

.hfilter.grow {
  flex: 1;
  min-width: 220px;
}

.content-search {
  width: 260px;
}

.hfilter:focus {
  border-color: rgba(25, 151, 198, 0.85);
  box-shadow: 0 0 0 2px rgba(25, 151, 198, 0.12);
}

.btn-refresh {
  min-width: 84px;
  height: 31px;
  border: 1px solid rgba(25, 151, 198, 0.8);
  border-radius: 6px;
  background: rgba(25, 151, 198, 0.1);
  color: #1fb2e8;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  cursor: pointer;
}

.btn-refresh:hover:not(:disabled) {
  border-color: #1fb2e8;
  background: rgba(25, 151, 198, 0.2);
  color: #eaf7fc;
}

.btn-refresh:disabled {
  opacity: 0.55;
  cursor: default;
}

.content-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin: 0 0 12px;
  padding: 10px 12px;
  border: 1px solid rgba(67, 72, 87, 0.72);
  border-radius: 8px;
  background: rgba(8, 12, 18, 0.42);
}

.selection-controls,
.bulk-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 9px;
}

.bulk-actions {
  justify-content: flex-end;
  margin-left: auto;
}

.bulk-actions strong {
  color: #eaf0f7;
  font-size: 12.5px;
  white-space: nowrap;
}

.bulk-actions strong.muted {
  color: #7f899b;
}

.select-filtered,
.group-select {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #aeb5c5;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 0px !important;
}

.catalog-table-section {
  position: relative;
  z-index: 1;
  min-height: 160px;
}

.gc-table-shell {
  position: relative;
  border: 1px solid rgba(67, 72, 87, 0.82);
  border-radius: 8px;
  background: rgba(8, 12, 18, 0.48);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.025);
}

.gc-table-shell.is-loading {
  border-color: rgba(25, 151, 198, 0.45);
}

.gc-table-scroll {
  overflow-x: auto;
  border-radius: 8px;
}

.gc-admin-table {
  width: 100%;
  min-width: 1133px;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;
}

.gc-table-head {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.8);
  background: rgba(8, 12, 18, 0.98);
  color: #818b9d;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.07em;
  line-height: 1.2;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
}

.gc-table-cell {
  padding: 12px;
  border-bottom: 1px solid rgba(47, 51, 61, 0.78);
  color: #cfd4df;
  line-height: 1.38;
  vertical-align: middle;
}

.gc-table-empty {
  padding: 24px 18px;
  color: #768093;
  text-align: center;
}

.select-col {
  width: 24px;
}

.part-col {
  width: 28%;
}

.fields-col {
  width: 212px;
}

.complete-col {
  width: 100px;
}

.status-col {
  width: 110px;
}

.actions-col {
  width: 90px;
  text-align: right;
}

.content-group-row .gc-table-cell {
  padding: 9px 12px;
  background: rgba(14, 18, 27, 0.82);
  border-top: 1px solid rgba(67, 72, 87, 0.76);
}

.group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.group-toggle {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-width: 170px;
  border: 0;
  background: transparent;
  color: #dce1eb;
  cursor: pointer;
  font-size: 13px;
  font-weight: 800;
  text-align: left;
}

.group-complete {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #8f98aa;
  font-size: 12px;
  font-weight: 800;
}

.mini-progress,
.progress-track {
  overflow: hidden;
  border-radius: 999px;
  background: rgba(43, 51, 67, 0.78);
}

.mini-progress {
  width: 92px;
  height: 5px;
}

.mini-progress-fill,
.progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, rgba(25, 151, 198, 0.78), rgba(27, 201, 142, 0.72));
  transition: width 0.45s ease;
}

.content-row.selected {
  background: rgba(25, 151, 198, 0.045);
}

.content-row {
  cursor: pointer;
}

.content-row:hover {
  background: rgba(25, 151, 198, 0.065);
}

.part-cell {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr);
  gap: 11px;
  align-items: center;
}

.part-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(67, 72, 87, 0.84);
  border-radius: 7px;
  background: #e8edf3;
  cursor: pointer;
  text-decoration: none;
}

.part-thumb:hover,
.part-thumb:focus {
  border-color: rgba(25, 151, 198, 0.72);
  outline: none;
}

.part-thumb img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.part-thumb.is-empty {
  border-color: rgba(25, 151, 198, 0.28);
  background:
    radial-gradient(circle at 50% 20%, rgba(25, 151, 198, 0.14), transparent 58%),
    rgba(14, 18, 27, 0.86);
  color: #7bccee;
}

.part-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #7bccee;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.05em;
}

.part-copy {
  min-width: 0;
}

.part-link {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  overflow: hidden;
  color: #7bccee;
  cursor: pointer;
  font-weight: 700;
  line-height: 1.35;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.part-link:hover,
.part-link:focus {
  color: #9bdff5;
  outline: none;
  text-decoration: underline;
}

.muted-sub {
  display: block;
  margin-top: 2px;
  color: #7f899b;
  font-size: 11px;
}

.part-meta-line {
  display: block;
  margin-top: 3px;
  overflow: hidden;
  color: #8f98aa;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.field-dot-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.field-token {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 22px;
  padding: 0 7px;
  border: 1px solid rgba(117, 124, 146, 0.24);
  border-radius: 999px;
  background: rgba(20, 25, 35, 0.45);
  outline: none;
}

.field-token:focus {
  border-color: rgba(25, 151, 198, 0.85);
}

.field-dot {
  width: 9px;
  height: 9px;
  border: 1px solid rgba(143, 152, 170, 0.74);
  border-radius: 999px;
  background: transparent;
}

.field-token.present .field-dot {
  border-color: rgba(27, 201, 142, 0.74);
  background: rgba(27, 201, 142, 0.92);
}

.field-token.missing {
  border-color: rgba(67, 72, 87, 0.72);
  background: rgba(10, 13, 20, 0.36);
  opacity: 0.72;
}

.field-token.unverified {
  border-color: rgba(224, 192, 126, 0.44);
  background: rgba(224, 192, 126, 0.08);
}

.field-label {
  color: #8f98aa;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.field-token.present .field-label {
  color: #cfd4df;
}

.field-value {
  color: #cfd4df;
  font-size: 10.5px;
  font-weight: 800;
}

.meter-wrap {
  display: grid;
  gap: 5px;
  color: #8f98aa;
  font-size: 11px;
  font-weight: 800;
}

.progress-track.compact {
  width: 100%;
  height: 6px;
}

.mobile-fields {
  display: none;
}

.detail-toggle {
  width: max-content;
  border: 0;
  background: transparent;
  color: #1fb2e8;
  cursor: pointer;
  padding: 0;
  font-size: 11px;
  font-weight: 800;
}

.mobile-detail-row {
  display: none;
}

.mobile-field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
}

.mobile-field-item {
  padding: 8px 9px;
  border: 1px solid rgba(67, 72, 87, 0.78);
  border-radius: 7px;
  background: rgba(14, 18, 27, 0.72);
}

.mobile-field-item span {
  display: block;
  color: #8f98aa;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.mobile-field-item strong {
  display: block;
  margin-top: 4px;
  color: #f0a1af;
  font-size: 12px;
}

.mobile-field-item.present strong {
  color: #89dfbd;
}

.mobile-field-item.unverified {
  border-color: rgba(224, 192, 126, 0.4);
}

.status-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
}

.content-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 22px;
  padding: 1px 9px;
  border: 1px solid rgba(117, 124, 146, 0.34);
  border-radius: 999px;
  color: #aeb5c5;
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
}

.content-status.st-pending,
.content-status.st-running,
.content-status.st-queued {
  border-color: rgba(25, 151, 198, 0.5);
  background: rgba(25, 151, 198, 0.12);
  color: #7bccee;
}

.content-status.st-empty {
  border-color: rgba(117, 124, 146, 0.34);
  background: rgba(58, 63, 77, 0.14);
  color: #aeb5c5;
}

.content-status.st-needs_review {
  border-color: rgba(224, 192, 126, 0.42);
  background: rgba(224, 192, 126, 0.12);
  color: #e0c07e;
}

.content-status.st-generated {
  border-color: rgba(25, 151, 198, 0.42);
  background: rgba(25, 151, 198, 0.1);
  color: #7bccee;
}

.content-status.st-approved,
.content-status.st-completed {
  border-color: rgba(27, 201, 142, 0.42);
  background: rgba(27, 201, 142, 0.1);
  color: #89dfbd;
}

.content-status.st-failed,
.content-status.st-error {
  border-color: rgba(232, 79, 106, 0.44);
  background: rgba(232, 79, 106, 0.12);
  color: #f0a1af;
}

.inline-spinner {
  width: 10px;
  height: 10px;
  border: 2px solid rgba(123, 204, 238, 0.32);
  border-top-color: #7bccee;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.unverified-hint {
  color: #e0c07e;
  font-size: 11px;
  font-weight: 800;
}

.gc-act {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 7px;
  background: rgba(20, 25, 35, 0.7);
  color: #d7dce8;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 700;
  transition: border-color 0.16s ease, background 0.16s ease, color 0.16s ease;
}

.gc-act:hover:not(:disabled) {
  border-color: rgba(25, 151, 198, 0.7);
  background: rgba(25, 151, 198, 0.12);
  color: #eaf7fc;
}

.gc-act.primary {
  border-color: rgba(25, 151, 198, 0.8);
  background: rgba(25, 151, 198, 0.14);
  color: #1fb2e8;
}

.gc-act.archive {
  border-color: rgba(224, 192, 126, 0.42);
  background: rgba(224, 192, 126, 0.08);
  color: #e0c07e;
}

.gc-act.danger-soft {
  border-color: rgba(232, 79, 106, 0.5);
  background: rgba(232, 79, 106, 0.08);
  color: #f0a1af;
}

.gc-act.danger-soft:hover:not(:disabled) {
  border-color: rgba(232, 79, 106, 0.8);
  background: rgba(232, 79, 106, 0.16);
  color: #ffd7de;
}

.gc-act.subtle {
  color: #9aa3b4;
}

.gc-act:disabled {
  opacity: 0.5;
  cursor: default;
}

.row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-left: 6px;
  margin-bottom: 6px;
  height: 27px;
  padding: 0 11px;
  border: 1px solid rgba(67, 72, 87, 0.95);
  border-radius: 6px;
  background: rgba(20, 25, 35, 0.7);
  color: #cfd2da;
  cursor: pointer;
  font-size: 11.5px;
  font-weight: 700;
}

.row-btn:hover:not(:disabled) {
  border-color: rgba(25, 151, 198, 0.7);
  color: #eaf7fc;
}

.row-btn.review {
  border-color: rgba(25, 151, 198, 0.38);
  background: rgba(25, 151, 198, 0.08);
  color: #7bccee;
}

.row-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.pager-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  color: #8f98aa;
  font-size: 12px;
  font-weight: 800;
}

.review-loading {
  min-height: 220px;
}

.panel-error {
  margin-bottom: 12px;
  padding: 9px 11px;
  border: 1px solid rgba(232, 79, 106, 0.38);
  border-radius: 7px;
  background: rgba(232, 79, 106, 0.11);
  color: #f0a1af;
  font-size: 12px;
  font-weight: 800;
}

.review-content {
  color: #cfd2da;
}

.review-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(67, 72, 87, 0.82);
}

.review-head h5 {
  margin: 4px 0 0;
  color: #e4e9f2;
  font-size: 18px;
  font-weight: 800;
}

.review-meta {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 7px;
  color: #8f98aa;
  font-size: 12px;
  font-weight: 800;
}

.review-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.review-field,
.review-sources,
.review-unverified {
  min-width: 0;
  padding: 11px;
  border: 1px solid rgba(67, 72, 87, 0.84);
  border-radius: 8px;
  background: rgba(14, 18, 27, 0.72);
}

.review-field.unverified {
  border-color: rgba(224, 192, 126, 0.44);
  background: rgba(224, 192, 126, 0.08);
}

.review-label {
  display: block;
  color: #8f98aa;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.review-value {
  margin: 7px 0 0;
  color: #dce1eb;
  font-size: 12.5px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
}

.review-link {
  display: block;
  margin-top: 7px;
  color: #7bccee;
  font-size: 12.5px;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.issue-chip {
  padding: 2px 8px;
  border: 1px solid rgba(67, 72, 87, 0.7);
  border-radius: 6px;
  background: rgba(58, 63, 77, 0.3);
  color: #cfd4df;
  font-size: 11px;
}

.issue-chip.warn {
  border-color: rgba(224, 192, 126, 0.36);
  background: rgba(224, 192, 126, 0.12);
  color: #e0c07e;
}

.review-sources,
.review-unverified {
  margin-top: 10px;
}

.review-sources ul {
  margin: 8px 0 0;
  padding-left: 18px;
}

.review-sources li {
  margin-bottom: 5px;
  color: #aeb5c5;
  font-size: 12.5px;
}

.review-sources a {
  color: #7bccee;
}

.review-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(67, 72, 87, 0.82);
}

::v-deep(.modal-content) {
  border: 1px solid rgba(67, 72, 87, 0.9);
  border-radius: 10px;
  background:
    radial-gradient(circle at 20% 0%, rgba(25, 151, 198, 0.12), transparent 34%),
    rgba(9, 13, 20, 0.98);
  color: #cfd2da;
}

::v-deep(.modal-header),
::v-deep(.modal-footer) {
  border-color: rgba(67, 72, 87, 0.82);
}

::v-deep(.modal-title) {
  color: #e4e9f2;
  font-size: 16px;
  font-weight: 800;
}

::v-deep(.close) {
  color: #cfd2da;
  text-shadow: none;
}

@media (max-width: 1080px) {
  .kpi-row {
    grid-template-columns: repeat(3, 1fr);
  }

  .tabs-row {
    align-items: stretch;
  }

  .content-filters {
    width: 100%;
  }

  .content-toolbar,
  .bulk-actions {
    justify-content: flex-start;
  }

  .bulk-actions {
    width: 100%;
    margin-left: 0;
    padding-top: 10px;
    border-top: 1px solid rgba(67, 72, 87, 0.62);
  }
}

@media (max-width: 760px) {
  .kpi-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .gc-admin-table {
    min-width: 760px;
  }

  .desktop-fields,
  .complete-col {
    display: none;
  }

  .mobile-fields {
    display: grid;
    gap: 7px;
  }

  .mobile-detail-row {
    display: table-row;
  }

  .fields-col {
    width: 170px;
  }

  .part-col {
    width: 34%;
  }

  .group-head,
  .review-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .review-grid,
  .mobile-field-grid {
    grid-template-columns: 1fr;
  }

  .content-job-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .job-controls,
  .job-controls label,
  .job-control-input,
  .job-limit {
    width: 100%;
  }

  .job-controls .gc-act {
    flex: 1 1 calc(50% - 9px);
  }

  .filter-panel-head,
  .selection-controls,
  .bulk-actions {
    align-items: stretch;
  }

  .selection-controls,
  .bulk-actions {
    width: 100%;
  }

  .selection-controls .gc-act,
  .bulk-actions .gc-act {
    flex: 1 1 auto;
  }

  .bulk-actions strong {
    display: flex;
    align-items: center;
    width: 100%;
  }
}
</style>
